/**
 * Finance palette — scopes, commands, and an @finance search provider for
 * the global command palette. Pattern mirrors srs/palette.ts.
 */
import { ArrowsDownUp, SealCheck, Gift, SignIn, Bank, Wallet } from "@phosphor-icons/react";

import type {
  Command,
  FeaturePalette,
  Result,
  ScopeNode,
  SearchProvider,
} from "@/shared/components/command-palette/types";

import { listTransactions } from "./api";

// ===================
// Scopes — taxonomy
// ===================

const FINANCE_PARENT: ScopeNode = {
  id: "finance",
  parentId: null,
  title: "Finance",
};

const scopes: ScopeNode[] = [
  {
    id: "finance.dashboard",
    parentId: "finance",
    title: "Dashboard",
    icon: Wallet,
    href: "/finance",
    keywords: ["money", "overview", "accounts"],
  },
  {
    id: "finance.transactions",
    parentId: "finance",
    title: "Transactions",
    icon: ArrowsDownUp,
    href: "/finance/transactions",
    keywords: ["ledger", "spend", "charges"],
  },
  {
    id: "finance.imports",
    parentId: "finance",
    title: "Imports",
    icon: SignIn,
    href: "/finance/imports",
    keywords: ["statement", "pdf", "upload", "csv", "receipt"],
  },
  {
    id: "finance.review",
    parentId: "finance",
    title: "Review queue",
    icon: SealCheck,
    href: "/finance/review",
    keywords: ["extraction", "confirm", "staged"],
  },
  {
    id: "finance.rewards",
    parentId: "finance",
    title: "Rewards",
    icon: Gift,
    href: "/finance/rewards",
    keywords: ["points", "cashback", "regret"],
  },
];

// ===================
// Search provider — @spend <q> over descriptors
// ===================

const transactionsProvider: SearchProvider = {
  id: "finance",
  label: "Transactions",
  icon: ArrowsDownUp,
  prefix: "@spend",
  async search(query, signal) {
    if (!query) return [];
    try {
      const page = await listTransactions({ search: query, limit: 8 });
      if (signal.aborted) return [];
      return page.items.map<Result>((txn) => ({
        id: `finance:${txn.id}`,
        title:
          txn.merchant_name ??
          txn.normalized_descriptor ??
          txn.raw_descriptor,
        subtitle: `${txn.amount_display} · ${new Date(txn.occurred_at).toLocaleDateString()}`,
        icon: ArrowsDownUp,
        scopeChain: [FINANCE_PARENT],
        score: 50,
        onSelect: (ctx) => ctx.router.push("/finance/transactions"),
      }));
    } catch {
      return [];
    }
  },
};

// ===================
// Commands
// ===================

const commands: Command[] = [
  {
    id: "finance.open-dashboard",
    title: "Open finance dashboard",
    icon: Wallet,
    scopeId: "finance",
    keywords: ["money", "accounts", "balances"],
    run: (ctx) => ctx.router.push("/finance"),
  },
  {
    id: "finance.import-statements",
    title: "Import statements",
    icon: SignIn,
    scopeId: "finance",
    keywords: ["upload", "pdf", "statement", "csv", "receipt"],
    run: (ctx) => ctx.router.push("/finance/imports"),
  },
  {
    id: "finance.review-extractions",
    title: "Review extracted transactions",
    icon: SealCheck,
    scopeId: "finance",
    keywords: ["review", "staged", "confirm"],
    run: (ctx) => ctx.router.push("/finance/review"),
  },
  {
    id: "finance.browse-ledger",
    title: "Browse transactions",
    icon: ArrowsDownUp,
    scopeId: "finance",
    keywords: ["ledger", "search", "spend"],
    run: (ctx) => ctx.router.push("/finance/transactions"),
  },
  {
    id: "finance.connect-bank",
    title: "Connect a bank",
    icon: Bank,
    scopeId: "finance",
    keywords: ["plaid", "link", "oauth", "simplefin"],
    run: (ctx) => ctx.router.push("/finance"),
  },
];

export const financePalette: FeaturePalette = {
  scopes,
  commands,
  searchProviders: [transactionsProvider],
};
